/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable complexity */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
// @ts-ignore
import 'react-quill/dist/quill.snow.css';
import NoData from 'src/@vymo/ui/blocks/noData';
import classnames from 'classnames';
import Emoji from 'quill-emoji';
import ImageDropAndPaste from 'quill-image-drop-and-paste';
import {
  defaultToolbarConfig,
  EMPTY_RICH_TEXT_VALUE,
  EMPTY_TOOLBAR_HANDLERS,
} from './constants';
import {
  getAllMappedEditorOptions,
  getEditorOptionTypes,
  getFilteredTriggerOptions,
  getHeaderNode,
  getOptionInsertionIndex,
  getOptionNodeDetails,
  getOptionSelectionIndex,
  getOptionsSelectedFromQuill,
  getQuillModules,
  getTextAreaValue,
  getTriggerBoxPosition,
  MaxLength,
  RichEditorOptionBlot,
  shouldAppendTriggerQuery,
  shouldCloseTriggerBox,
} from './queries';
import RichTextHeader from './richTextHeader';
import {
  HeaderButton,
  RichTextEditorOption,
  RichTextEditorProps,
} from './types';
import styles from './index.module.scss';
// @ts-ignore
import 'quill-emoji/dist/quill-emoji.css';

const { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } = Emoji;
Quill.register('modules/imageDropAndPaste', ImageDropAndPaste);
Quill.register('modules/maxLength', MaxLength);
Quill.register(RichEditorOptionBlot);
Quill.register(
  {
    'formats/emoji': EmojiBlot,
    'modules/emoji-toolbar': ToolbarEmoji,
    'modules/emoji-textarea': TextAreaEmoji,
    'modules/emoji-shortname': ShortNameEmoji,
  },
  true,
);

function RichTextEditor({
  maxLength,
  description,
  headerText,
  headerButtons,
  onChange,
  selectOptionsMap,
  richTextHtml,
  classNames = '',
  placeholder = '',
  legacy = false,
  menuLabel = '',
  hasHeader = false,
  mappedMenuOptions = [],
  allTriggerOptions = [],
  mappedTriggerOptions = [],
  removeSpaceFromValue = false,
  removeWhiteSpaceFromValue = false,
  showToolbar = true,
  singleLineInput = false,
  disableTextFormatting = false,
  toolbarConfig = defaultToolbarConfig,
  toolbarHandlers = EMPTY_TOOLBAR_HANDLERS,
  customToolbarActions,
  disabled = false,
  setActiveTriggerChar,
  'data-test-id': dataTestId = 'richText',
  viewMode = false,
}: RichTextEditorProps) {
  const [value, setValue] = useState<string>(EMPTY_RICH_TEXT_VALUE);
  const [showTriggerBox, setShowTriggerBox] = useState(false);
  const [triggerQuery, setTriggerQuery] = useState('');
  const [toolbarElement, setToolbarElement] = useState<HTMLElement | null>(
    null,
  );
  const [triggerBoxPosition, setTriggerBoxPosition] = useState({
    top: 0,
    left: 0,
  });
  const hasBlurredRef = useRef(false);
  const triggerStartIndexRef = useRef<number | null>(null);
  const quillRef = useRef<ReactQuill | null>(null);
  const richTextEditorRef = useRef<HTMLDivElement | null>(null);
  const formats = disableTextFormatting ? [] : undefined;
  const getEditor = useCallback(() => quillRef.current?.getEditor(), []);
  const shouldShowCustomToolbarActions =
    Boolean(customToolbarActions) && showToolbar !== false && !viewMode;
  const richTextEditorClassName = `${styles.richTextEditor} ${
    hasHeader ? styles['richTextEditor--withHeader'] : ''
  }`;
  const modules = useMemo(
    () =>
      getQuillModules({
        maxLength,
        showToolbar,
        toolbarConfig,
        toolbarHandlers,
        disableTextFormatting,
        singleLineInput,
        getEditor,
        viewMode,
      }),
    [
      maxLength,
      showToolbar,
      toolbarConfig,
      toolbarHandlers,
      disableTextFormatting,
      singleLineInput,
      getEditor,
      viewMode,
    ],
  );
  const allMappedEditorOptions = useMemo(
    () => getAllMappedEditorOptions(mappedMenuOptions, allTriggerOptions),
    [mappedMenuOptions, allTriggerOptions],
  );

  const allEditorOptionTypes = useMemo(
    () => getEditorOptionTypes(allMappedEditorOptions),
    [allMappedEditorOptions],
  );

  const filteredTriggerOptions = useMemo(
    () => getFilteredTriggerOptions(mappedTriggerOptions, triggerQuery),
    [mappedTriggerOptions, triggerQuery],
  );

  const closeTriggerBox = useCallback(() => {
    setShowTriggerBox(false);
    setTriggerQuery('');
    triggerStartIndexRef.current = null;
    setActiveTriggerChar(null);
  }, [setActiveTriggerChar]);

  const onRichEditorChange = useCallback(
    (content) => {
      const editor = getEditor();
      if (!editor) return;
      const plainText = editor.getText();

      if (maxLength && plainText.length - 1 > maxLength) return;
      setValue(content);
      const editorRootNodes = editor.root;
      const editorChildNodes = editor.root.childNodes || [];
      onChange?.(
        getTextAreaValue(
          editorChildNodes as NodeList,
          removeSpaceFromValue,
          removeWhiteSpaceFromValue,
          allMappedEditorOptions,
          legacy,
        ),
        undefined,
        {
          data: {
            richTextHtml: content,
            optionsSelected: getOptionsSelectedFromQuill(
              allEditorOptionTypes,
              legacy,
              allMappedEditorOptions,
              editorRootNodes,
            ),
          },
        },
      );
    },
    [
      maxLength,
      onChange,
      removeSpaceFromValue,
      removeWhiteSpaceFromValue,
      allEditorOptionTypes,
      getEditor,
      legacy,
      allMappedEditorOptions,
    ],
  );

  const onHeaderButtonClick = useCallback(
    (selectedHeaderButton: HeaderButton) => {
      const editor = getEditor();
      if (!editor) return;
      const selection = editor.getSelection();
      const index = selection?.index ?? 0;
      const { actionData } = selectedHeaderButton;
      const headerNode = getHeaderNode(selectedHeaderButton);

      editor.clipboard.dangerouslyPasteHTML(index, headerNode, 'user');
      editor.setSelection(index + actionData.length + 1, 0, 'silent');
    },
    [getEditor],
  );

  const onClickOption = useCallback(
    (
      editor: Quill | undefined,
      index: number,
      option: RichTextEditorOption,
      fromHeader?: boolean,
    ) => {
      if (!editor) return;

      const { displayContent, optionNode } = getOptionNodeDetails(
        option,
        legacy,
      );
      const insertionIndex = getOptionInsertionIndex({
        index,
        fromHeader,
        triggerStartIndex: triggerStartIndexRef.current,
        triggerQuery,
      });

      if (!fromHeader) {
        editor.deleteText(insertionIndex, triggerQuery.length + 1, 'user');
        closeTriggerBox();
      }

      editor.clipboard.dangerouslyPasteHTML(insertionIndex, optionNode, 'user');
      editor.setSelection(
        getOptionSelectionIndex({
          insertionIndex,
          fromHeader,
          legacy,
          optionNode,
          displayContent,
        }),
        0,
        'silent',
      );
    },
    [closeTriggerBox, legacy, triggerQuery],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const pressedKey = e.key;

    if (Object.keys(selectOptionsMap || {}).includes(pressedKey)) {
      const editor = getEditor();
      if (!editor) return;

      const range = editor.getSelection();
      if (!range) return;

      const bounds = editor.getBounds(range.index);
      setTriggerBoxPosition(getTriggerBoxPosition(bounds));

      triggerStartIndexRef.current = range.index;
      setTriggerQuery('');
      setShowTriggerBox(true);
      setActiveTriggerChar(pressedKey);
      return;
    }

    if (!showTriggerBox) return;

    if (pressedKey === 'Backspace') {
      if (!triggerQuery) {
        closeTriggerBox();
        return;
      }

      setTriggerQuery((query) => query.slice(0, -1));
      return;
    }

    if (shouldCloseTriggerBox(pressedKey)) {
      closeTriggerBox();
      return;
    }

    if (shouldAppendTriggerQuery(e)) {
      setTriggerQuery((query) => `${query}${pressedKey}`);
    }
  };

  useEffect(() => {
    const editor = getEditor();
    const root = editor?.root;
    if (
      editor &&
      root &&
      document.activeElement === root &&
      !hasBlurredRef.current
    ) {
      hasBlurredRef.current = true;
      root.blur();
    }
  }, [getEditor]);

  useEffect(() => {
    setValue(richTextHtml || '');
  }, [richTextHtml]);

  useEffect(() => {
    if (!shouldShowCustomToolbarActions) {
      setToolbarElement(null);
      return;
    }

    const toolbarNode =
      richTextEditorRef.current?.querySelector<HTMLElement>('.ql-toolbar') ??
      null;

    setToolbarElement(toolbarNode);
  }, [shouldShowCustomToolbarActions, toolbarConfig]);

  return (
    <div>
      {Boolean(description) && !viewMode && (
        <div
          data-test-id={`${dataTestId}-subheader`}
          className={styles.richTextEditor__subHeader}
        >
          {description}
        </div>
      )}

      {hasHeader && !viewMode && (
        <RichTextHeader
          dataTestId={dataTestId}
          getEditor={getEditor}
          headerText={headerText}
          headerButtons={headerButtons}
          onHeaderButtonClick={onHeaderButtonClick}
          menuOptions={mappedMenuOptions}
          menuLabel={menuLabel}
          onClickOption={onClickOption}
        />
      )}

      <div
        ref={richTextEditorRef}
        className={richTextEditorClassName}
        style={{ position: 'relative' }}
        data-test-id={`${dataTestId}-editor`}
      >
        <ReactQuill
          key={showToolbar ? 'with-toolbar' : 'no-toolbar'}
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onRichEditorChange}
          onKeyDown={handleKeyDown}
          modules={modules}
          className={classnames(styles.richTextEditorContainer, classNames)}
          placeholder={placeholder}
          formats={formats}
          readOnly={disabled || viewMode}
          preserveWhitespace
          style={{
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        />
        {toolbarElement && shouldShowCustomToolbarActions
          ? createPortal(
              <span
                className={classnames(
                  'ql-formats',
                  styles.richTextEditor__toolbarActions,
                )}
                data-test-id={`${dataTestId}-toolbar-actions`}
              >
                {customToolbarActions?.({ getEditor })}
              </span>,
              toolbarElement,
            )
          : null}
        {showTriggerBox && (
          <div
            className={styles.triggerBox}
            style={{
              top: triggerBoxPosition.top,
              left: triggerBoxPosition.left,
            }}
            onBlur={closeTriggerBox}
            data-test-id={`${dataTestId}-optionList`}
          >
            {filteredTriggerOptions.map((option) => (
              <div
                key={String(option.code ?? option.value)}
                onClick={() => {
                  const editor = getEditor();
                  if (!editor) return;
                  const index =
                    editor.getSelection()?.index ??
                    Math.max(0, editor.getLength() - 1);
                  onClickOption(editor, index, option);
                }}
                data-test-id={`${dataTestId}-optionList-${
                  option.code ?? option.value
                }`}
                className={styles.triggerBox__option}
              >
                {option.label || option.name}
              </div>
            ))}
            {filteredTriggerOptions.length === 0 && <NoData />}
          </div>
        )}
      </div>
    </div>
  );
}

export default RichTextEditor;
